<template>
  <div class="settings-page">
    <FilterConfig
      v-if="editingPanel === 'filter' && editingId !== null && editingDatabase"
      :filter="editingFilter"
      :data-source="editingDatabase"
      :available-fields="editingAvailableFields"
      @back="closeEditing"
      @close="closeEditing"
      @update:filter="saveFilter"
    />
    <StatusMappingConfig
      v-else-if="
        editingPanel === 'status' && editingId !== null && editingDatabase
      "
      :status-mapping="editingStatusMapping"
      :database="editingDatabase"
      @back="closeEditing"
      @close="closeEditing"
      @update:status-mapping="saveStatusMapping"
    />
    <ScheduledMappingConfig
      v-else-if="
        editingPanel === 'scheduled' && editingId !== null && editingDatabase
      "
      :scheduled-mapping="editingScheduledMapping"
      :database="editingDatabase"
      @back="closeEditing"
      @close="closeEditing"
      @update:scheduled-mapping="saveScheduledMapping"
    />
    <UrlMappingConfig
      v-else-if="
        editingPanel === 'url' && editingId !== null && editingDatabase
      "
      :url-mapping="editingUrlMapping"
      :database="editingDatabase"
      @back="closeEditing"
      @close="closeEditing"
      @update:url-mapping="saveUrlMapping"
    />
    <template v-else>
      <h2>Task Bindings</h2>
      <div class="subtitle">
        Configure which database tables contribute tasks to this organization's
        workspaces.
      </div>

      <section class="bindings-block">
        <div
          v-for="workspace in workspaces"
          :key="workspace.id"
          class="workspace-section"
        >
          <button
            v-if="!isPersonalWorkspace(workspace.id)"
            class="workspace-header"
            @click="toggleWorkspace(workspace.id)"
          >
            <ChevronRightIcon
              class="chevron-icon"
              :class="{ expanded: !collapsedWorkspaces[workspace.id] }"
            />
            <span class="workspace-name">{{ workspace.name }}</span>
            <span class="binding-count">{{
              bindingsFor(workspace.id).length
            }}</span>
          </button>

          <div
            v-if="
              isPersonalWorkspace(workspace.id) ||
              !collapsedWorkspaces[workspace.id]
            "
            class="workspace-content"
            :class="{ 'no-indent': isPersonalWorkspace(workspace.id) }"
          >
            <div
              v-for="[id, binding] in bindingsFor(workspace.id)"
              :key="id"
              class="entry"
            >
              <div class="binding-info">
                <BaseTypeIcon :image="databaseIcon(binding)" size="xs" plain />
                <span class="db-name">{{ databaseName(binding) }}</span>
              </div>
              <div class="binding-actions">
                <BaseButton
                  kind="auxiliary"
                  :text="binding.filter ? 'Filtered' : 'No filter'"
                  @click="() => openEditFilter(id, binding)"
                >
                  <template #icon>
                    <FilterIcon />
                  </template>
                </BaseButton>
                <BaseButton
                  kind="auxiliary"
                  :text="binding.status_mapping ? 'Status mapped' : 'No status'"
                  @click="() => openEditStatus(id, binding)"
                />
                <BaseButton
                  kind="auxiliary"
                  :text="
                    binding.scheduled_mapping
                      ? 'Scheduled mapped'
                      : 'No schedule'
                  "
                  @click="() => openEditScheduled(id, binding)"
                />
                <BaseButton
                  kind="auxiliary"
                  :text="binding.url_mapping ? 'URL mapped' : 'No URL'"
                  @click="() => openEditUrl(id, binding)"
                />
                <BaseButton
                  kind="main"
                  type="secondary"
                  tint="red"
                  text="Remove"
                  @click="() => removeBinding(id)"
                />
              </div>
            </div>

            <div class="add-form">
              <BaseInputSelect
                :value="selectedTableByWorkspace[workspace.id] ?? ''"
                :options="databaseOptions(workspace.id)"
                placeholder="Select a database…"
                :select-only="true"
                :searchable="true"
                @update:value="(t) => onDatabaseSelected(workspace.id, t)"
              />
              <BaseButton
                kind="main"
                type="primary"
                text="Add"
                :state="
                  selectedTableByWorkspace[workspace.id]
                    ? 'default'
                    : 'disabled'
                "
                @click="() => addBinding(workspace.id)"
              />
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref, type Component } from "vue";

import type {
  ColumnId,
  GraphDatabase,
  GraphViewWithHintsColumn,
  TaskBinding,
  TaskBindingConfig,
  TaskBindingDictionary,
  TaskBindingDictionaryDictionary,
  TaskBindingStatusMapping,
  ViewFilterFormula,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseTypeIcon from "@/components/base/BaseTypeIcon.vue";
import { getTypeIcon } from "@/components/databases";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import FilterIcon from "@/components/icons/FilterIcon.vue";
import * as inject from "@/components/inject";
import ScheduledMappingConfig from "@/components/settings/ScheduledMappingConfig.vue";
import StatusMappingConfig from "@/components/settings/StatusMappingConfig.vue";
import UrlMappingConfig from "@/components/settings/UrlMappingConfig.vue";
import FilterConfig from "@/components/views/config/FilterConfig.vue";
import { useReleaser } from "@/ref";

interface Workspace {
  id: WorkspaceId;
  name: string;
}

export default defineComponent({
  components: {
    BaseButton,
    BaseInputSelect,
    BaseTypeIcon,
    ChevronRightIcon,
    FilterConfig,
    FilterIcon,
    ScheduledMappingConfig,
    StatusMappingConfig,
    UrlMappingConfig,
  },
  props: {
    orgId: { type: String, required: true },
    workspaceId: { type: String, required: true },
  },
  async setup(props) {
    const controller = inject.controller();
    const workspaceDatabases = inject.workspaceDatabases();
    const profile = inject.profile();

    // Scopes the binding-editing panels; they only render while a binding is
    // being edited (which sets this to the binding's workspace), so the
    // initial personal workspace is never observed. Must be before any await.
    const editingWorkspaceId = ref<WorkspaceId>(
      profile.value.user.personal.workspaceId
    );
    inject.currentWorkspaceId(editingWorkspaceId);
    const releaser = useReleaser();

    const bindingsByWorkspace = await releaser.add(
      controller.task.binding.byWorkspace()
    );

    // The organization's workspaces, with names.
    const workspaceIdsRef = await controller.organization.workspaces.all(
      props.orgId
    );
    const workspaceIds = workspaceIdsRef.release() ?? [];
    const workspaceEntries = await Promise.all(
      workspaceIds.map(async (id: WorkspaceId) => {
        const ref = await controller.workspace.get(id);
        const name = ref.value?.name ?? id;
        ref.release();
        return { id, name };
      })
    );
    const workspaces = computed<Workspace[]>(() =>
      [...workspaceEntries].sort((a, b) => a.name.localeCompare(b.name))
    );

    const personalWorkspaceId = computed(
      () => profile.value.user.personal.workspaceId
    );
    const isPersonalWorkspace = (wsId: string) =>
      wsId === personalWorkspaceId.value;

    const collapsedWorkspaces = reactive<Record<string, boolean>>({});
    const toggleWorkspace = (wsId: string) => {
      collapsedWorkspaces[wsId] = !collapsedWorkspaces[wsId];
    };

    const bindingsFor = (wsId: string): [string, TaskBinding][] => {
      const dict: TaskBindingDictionaryDictionary =
        bindingsByWorkspace.value ?? {};
      const wsBindings: TaskBindingDictionary = dict[wsId] ?? {};
      return Object.entries(wsBindings).sort(([, a], [, b]) =>
        databaseName(a).localeCompare(databaseName(b))
      );
    };

    const dbFor = (binding: TaskBinding): GraphDatabase | null =>
      workspaceDatabases.value?.[binding.workspace_id]?.[binding.table_id] ??
      null;

    const databaseName = (binding: TaskBinding) =>
      dbFor(binding)?.data_name ?? binding.table_id;

    const databaseIcon = (binding: TaskBinding): Component | string =>
      getTypeIcon(profile.value, dbFor(binding));

    // Create state: the table selected per workspace.
    const selectedTableByWorkspace = reactive<Record<string, string>>({});

    const databaseOptions = (wsId: string): SelectOption[] => {
      const dbs = Object.values(workspaceDatabases.value?.[wsId] ?? {});
      return dbs
        .sort((a, b) => a.data_name.localeCompare(b.data_name))
        .map((db) => ({ value: db.id, label: db.data_name, aliases: [] }));
    };

    const onDatabaseSelected = (wsId: string, tableId: string) => {
      selectedTableByWorkspace[wsId] = tableId;
    };

    const addBinding = async (wsId: string) => {
      const tableId = selectedTableByWorkspace[wsId];
      if (!tableId) return;
      const config: TaskBindingConfig = {
        workspace_id: wsId,
        table_id: tableId,
      };
      await controller.workspace.taskBindings.add(wsId, tableId, config);
      selectedTableByWorkspace[wsId] = "";
    };

    const removeBinding = async (id: string) => {
      await controller.task.binding.remove(id);
    };

    // Editing state (mirrors the personal Task Bindings page).
    const editingPanel = ref<"filter" | "status" | "scheduled" | "url" | null>(
      null
    );
    const editingId = ref<string | null>(null);
    const editingFilter = ref<ViewFilterFormula | undefined>(undefined);
    const editingStatusMapping = ref<TaskBindingStatusMapping | undefined>(
      undefined
    );
    const editingScheduledMapping = ref<ColumnId | undefined>(undefined);
    const editingUrlMapping = ref<ColumnId | undefined>(undefined);
    const editingDatabase = ref<GraphDatabase | null>(null);
    const editingAvailableFields = ref<
      Record<string, GraphViewWithHintsColumn>
    >({});

    const loadEditingFields = async (binding: TaskBinding) => {
      const db = dbFor(binding);
      editingDatabase.value = db;
      if (db) {
        const result = await controller.graph.view.ofQuery(
          binding.workspace_id,
          db.name
        );
        if ("value" in result) {
          editingAvailableFields.value = result.value.available_columns;
        }
      }
    };

    const openEditFilter = async (id: string, binding: TaskBinding) => {
      editingId.value = id;
      editingFilter.value = binding.filter;
      editingWorkspaceId.value = binding.workspace_id;
      editingPanel.value = "filter";
      await loadEditingFields(binding);
    };

    const openEditStatus = async (id: string, binding: TaskBinding) => {
      editingId.value = id;
      editingStatusMapping.value = binding.status_mapping;
      editingWorkspaceId.value = binding.workspace_id;
      editingPanel.value = "status";
      await loadEditingFields(binding);
    };

    const openEditScheduled = async (id: string, binding: TaskBinding) => {
      editingId.value = id;
      editingScheduledMapping.value = binding.scheduled_mapping ?? undefined;
      editingWorkspaceId.value = binding.workspace_id;
      editingPanel.value = "scheduled";
      await loadEditingFields(binding);
    };

    const openEditUrl = async (id: string, binding: TaskBinding) => {
      editingId.value = id;
      editingUrlMapping.value = binding.url_mapping ?? undefined;
      editingWorkspaceId.value = binding.workspace_id;
      editingPanel.value = "url";
      await loadEditingFields(binding);
    };

    const currentConfig = (id: string): TaskBindingConfig | undefined => {
      const dict: TaskBindingDictionaryDictionary =
        bindingsByWorkspace.value ?? {};
      for (const wsBindings of Object.values(dict)) {
        const binding = wsBindings[id];
        if (binding) {
          // The upsert RPC takes a `TaskBindingConfig` (no `rows`), so strip it.
          const { rows: _rows, ...config } = binding;
          return config;
        }
      }
      return undefined;
    };

    const saveFilter = async (filter: ViewFilterFormula | undefined) => {
      const id = editingId.value;
      if (!id) return;
      const config = currentConfig(id);
      if (!config) return;
      await controller.task.binding.upsert(id, { ...config, filter });
    };

    const saveStatusMapping = async (
      status_mapping: TaskBindingStatusMapping | undefined
    ) => {
      const id = editingId.value;
      if (!id) return;
      const config = currentConfig(id);
      if (!config) return;
      await controller.task.binding.upsert(id, { ...config, status_mapping });
    };

    const saveScheduledMapping = async (
      scheduled_mapping: ColumnId | undefined
    ) => {
      const id = editingId.value;
      if (!id) return;
      const config = currentConfig(id);
      if (!config) return;
      await controller.task.binding.upsert(id, {
        ...config,
        scheduled_mapping,
      });
    };

    const saveUrlMapping = async (url_mapping: ColumnId | undefined) => {
      const id = editingId.value;
      if (!id) return;
      const config = currentConfig(id);
      if (!config) return;
      await controller.task.binding.upsert(id, { ...config, url_mapping });
    };

    const closeEditing = () => {
      editingPanel.value = null;
      editingId.value = null;
      editingDatabase.value = null;
      editingFilter.value = undefined;
      editingStatusMapping.value = undefined;
      editingScheduledMapping.value = undefined;
      editingUrlMapping.value = undefined;
      editingAvailableFields.value = {};
    };

    return {
      workspaces,
      isPersonalWorkspace,
      collapsedWorkspaces,
      toggleWorkspace,
      bindingsFor,
      databaseName,
      databaseIcon,
      selectedTableByWorkspace,
      databaseOptions,
      onDatabaseSelected,
      addBinding,
      removeBinding,
      editingPanel,
      editingId,
      editingFilter,
      editingStatusMapping,
      editingScheduledMapping,
      editingUrlMapping,
      editingDatabase,
      editingAvailableFields,
      openEditFilter,
      openEditStatus,
      openEditScheduled,
      openEditUrl,
      saveFilter,
      saveStatusMapping,
      saveScheduledMapping,
      saveUrlMapping,
      closeEditing,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.bindings-block {
  display: flex;
  flex-direction: column;
  row-gap: 4px;

  .workspace-section {
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;

    .workspace-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 4px;
      border: none;
      background: none;
      border-radius: $border-radius-m;
      transition: background-color 0.15s ease-out;

      @include themed() {
        color: t($primary);
      }

      &:hover {
        @include themed() {
          background-color: t($grey-05);
        }
      }
    }

    .workspace-name {
      @extend %font-body-medium;
      font-size: 15px;
    }

    .binding-count {
      @extend %font-body-regular;
      @include themed() {
        color: t($grey-40);
      }
    }

    .workspace-content {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      padding-inline-start: 30px;
      padding-bottom: 12px;

      &.no-indent {
        padding-inline-start: 0;
      }
    }
  }

  .chevron-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease;

    &.expanded {
      transform: rotate(90deg);
    }
  }
}

.entry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  .binding-info {
    display: flex;
    align-items: center;
    gap: 8px;

    .db-name {
      @extend %font-body-medium;
    }
  }

  .binding-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.add-form {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
}
</style>
